import { useEffect, useMemo, useState } from "react";
import { api, formatMoney, formatDate, formatApiError } from "@/lib/api";
import { displayFor, uiToBackendStatus } from "@/lib/bills-util";
import { toast } from "sonner";
import { X, CheckCircle2, Clock, XCircle, ExternalLink, Zap, Sparkles } from "lucide-react";
import BillAmountCell from "@/components/BillAmountCell";

// Step 3 of the weekly reconciliation ritual. A focused card-stack walkthrough
// of every Pending / Unpaid bill in the current pay period whose due date is
// on or before today. One decision at a time with a progress bar and a
// celebration summary at the end. Backed by:
//   • PATCH /api/bills/{id}           — flip status
//   • POST  /api/reconcile/complete   — stamp last_reconciled_at
export default function ReconcileBillsFlow({ bills, currentPP, onClose, onBillUpdated, onCompleted }) {
  const today = useMemo(() => { const t = new Date(); t.setHours(0, 0, 0, 0); return t; }, []);

  // Queue = eligible bills for the reconciliation ritual.
  //   (1) IN the current pay period, due on or before today, status unpaid/pending
  //   (2) PLUS prior-period carryover bills (any date before this period) with
  //       status unpaid or pending — these are "am I actually done?" targets
  //       that would otherwise never surface for reconciliation.
  const [queue, setQueue] = useState(() => {
    if (!currentPP) return [];
    const ps = new Date(currentPP.period_start + "T00:00:00");
    const pe = new Date(currentPP.period_end + "T00:00:00");
    return (bills || [])
      .filter((b) => {
        const d = new Date(b.due_date + "T00:00:00");
        const inCurrent = d >= ps && d <= pe && d <= today;
        const isPriorCarryover = d < ps;
        if (!inCurrent && !isPriorCarryover) return false;
        return b.status === "unpaid" || b.status === "pending";
      })
      .sort((a, b) => a.due_date.localeCompare(b.due_date));
  });

  const updateSnapshotBill = (updated) => {
    setQueue((prev) => prev.map((x) => (x.id === updated.id ? { ...x, ...updated } : x)));
  };

  const [i, setI] = useState(0);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [tally, setTally] = useState({ cleared: 0, pending: 0, skipped: 0, clearedAmount: 0 });
  const [finishing, setFinishing] = useState(false);

  const current = queue[i];
  const progressPct = queue.length === 0 ? 100 : Math.round((i / queue.length) * 100);

  const applyDecision = async (uiStatus) => {
    if (!current) return;
    if (uiStatus === "skip") {
      setTally((t) => ({ ...t, skipped: t.skipped + 1 }));
      advance();
      return;
    }
    setBusy(true);
    try {
      const backend = uiToBackendStatus(uiStatus);
      const upd = { status: backend };
      if (["paid", "cleared", "pending"].includes(backend)) upd.paid_amount = current.estimated_due_amount;
      const { data } = await api.patch(`/bills/${current.id}`, upd);
      onBillUpdated && onBillUpdated(data);
      updateSnapshotBill(data);
      setTally((t) => {
        if (uiStatus === "Paid") return { ...t, cleared: t.cleared + 1, clearedAmount: t.clearedAmount + Number(current.estimated_due_amount) };
        if (uiStatus === "Pending") return { ...t, pending: t.pending + 1 };
        return t;
      });
      advance();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const advance = () => {
    setI((prev) => {
      const next = prev + 1;
      if (next >= queue.length) setDone(true);
      return next;
    });
  };

  const finish = async () => {
    setFinishing(true);
    try {
      const { data } = await api.post("/reconcile/complete");
      onCompleted && onCompleted(data);
      onClose();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setFinishing(false); }
  };

  // Empty state — nothing to reconcile this pay period.
  if (queue.length === 0) {
    return (
      <ModalShell onClose={onClose} testId="reconcile-modal-empty">
        <EmptyCelebration onClose={onClose} periodLabel={currentPP ? `${formatDate(currentPP.period_start)} – ${formatDate(currentPP.period_end)}` : ""} />
      </ModalShell>
    );
  }

  if (done) {
    return (
      <ModalShell onClose={onClose} testId="reconcile-modal-done">
        <FinishSummary tally={tally} total={queue.length} onFinish={finish} finishing={finishing} />
      </ModalShell>
    );
  }

  // Belt-and-braces: if the index ever advances past the snapshot (should not
  // happen with the useState snapshot, but guards against future refactors),
  // roll into the done state cleanly instead of crashing on displayFor().
  if (!current) {
    return (
      <ModalShell onClose={onClose} testId="reconcile-modal-done">
        <FinishSummary tally={tally} total={queue.length} onFinish={finish} finishing={finishing} />
      </ModalShell>
    );
  }

  const disp = displayFor(current, today);

  return (
    <ModalShell onClose={onClose} testId="reconcile-modal">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-widest text-[#4A6148]">Step 3 · Reconcile bills</div>
          <h2 className="font-heading text-xl font-medium text-[#2C302E] mt-0.5">Which bills have cleared?</h2>
          <p className="text-xs text-[#59605C] mt-1">
            Open your bank feed alongside this. For each bill, tap what actually happened.
          </p>
        </div>
        <button className="text-[#8A918D] hover:text-[#2C302E]" onClick={onClose} data-testid="reconcile-close" aria-label="Close">
          <X size={18} />
        </button>
      </div>

      {/* Progress */}
      <div className="mb-4">
        <div className="flex justify-between text-[10px] font-semibold uppercase tracking-widest text-[#8A918D] mb-1">
          <span data-testid="reconcile-progress-count">Bill {i + 1} of {queue.length}</span>
          <span>{progressPct}%</span>
        </div>
        <div className="h-1.5 rounded-full bg-[#EBE6E0] overflow-hidden">
          <div className="h-full bg-[#4A6148] transition-all duration-300" style={{ width: `${progressPct}%` }} data-testid="reconcile-progress-bar" />
        </div>
      </div>

      {/* Bill card */}
      <div className={`p-5 rounded-2xl border-2 ${disp.notCleared ? "bg-[#FDF2F0]/60 border-[#E5B4A3]" : "bg-[#F9F8F6] border-[#EBE6E0]"}`} data-testid={`reconcile-current-${current.id}`}>
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <div className="flex items-center gap-1.5 flex-wrap">
              <span className="font-heading text-lg text-[#2C302E]" data-testid="reconcile-current-name">{current.name}</span>
              {current.is_autopay && (
                <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-[#EEF3EC] text-[#3B5B3E]">
                  <Zap size={9} /> autopay
                </span>
              )}
              {current.pay_url && (
                <a
                  href={current.pay_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-0.5 text-[10px] font-semibold uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-[#EEF3EC] text-[#3B5B3E] hover:bg-[#D5E1D3]"
                  data-testid="reconcile-current-pay-url"
                >
                  <ExternalLink size={9} /> pay online
                </a>
              )}
            </div>
            <div className="text-xs text-[#59605C] mt-1">
              Due {formatDate(current.due_date)} · currently <span className="font-medium uppercase">{current.status}</span>
            </div>
          </div>
          <BillAmountCell
            bill={current}
            size="lg"
            testIdPrefix="reconcile-current"
            onUpdated={(updated) => {
              onBillUpdated && onBillUpdated(updated);
              updateSnapshotBill(updated);
            }}
          />
        </div>
      </div>

      {/* Actions */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-4">
        <button
          onClick={() => applyDecision("Paid")}
          disabled={busy}
          className="flex items-center justify-center gap-1.5 py-3 rounded-xl bg-[#DDE9D8] border-2 border-[#8FBF88] text-[#1F6B2E] hover:bg-[#CCE1C6] disabled:opacity-60 transition-colors"
          data-testid="reconcile-btn-cleared"
        >
          <CheckCircle2 size={16} /> <span className="font-semibold text-sm">Cleared</span>
        </button>
        <button
          onClick={() => applyDecision("Pending")}
          disabled={busy}
          className="flex items-center justify-center gap-1.5 py-3 rounded-xl bg-[#F7EBD0] border-2 border-[#E0C579] text-[#8C5D30] hover:bg-[#F1DFB1] disabled:opacity-60 transition-colors"
          data-testid="reconcile-btn-pending"
        >
          <Clock size={16} /> <span className="font-semibold text-sm">Still pending</span>
        </button>
        <button
          onClick={() => applyDecision("skip")}
          disabled={busy}
          className="flex items-center justify-center gap-1.5 py-3 rounded-xl bg-[#F2EFEB] border-2 border-[#CDC7BB] text-[#59605C] hover:bg-[#E8E4DC] disabled:opacity-60 transition-colors"
          data-testid="reconcile-btn-skip"
        >
          <XCircle size={16} /> <span className="font-semibold text-sm">Not paid yet</span>
        </button>
      </div>

      <div className="text-[11px] text-[#8A918D] mt-3 text-center">
        Tip: open your bank statement in another tab — this only takes ~30 seconds.
      </div>
    </ModalShell>
  );
}

// ---------- Sub-components ----------
function ModalShell({ children, onClose, testId }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
      data-testid={`${testId}-backdrop`}
    >
      <div
        className="w-full max-w-lg bg-white rounded-2xl shadow-xl p-6"
        onClick={(e) => e.stopPropagation()}
        data-testid={testId}
      >
        {children}
      </div>
    </div>
  );
}

function EmptyCelebration({ onClose, periodLabel }) {
  return (
    <div className="text-center py-6" data-testid="reconcile-empty-body">
      <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#DDE9D8] text-[#1F6B2E] mb-3">
        <Sparkles size={26} />
      </div>
      <h2 className="font-heading text-xl font-medium text-[#2C302E]">Nothing to reconcile</h2>
      <p className="text-sm text-[#59605C] mt-2 max-w-sm mx-auto">
        No unpaid or pending bills due on or before today in this pay period ({periodLabel}). You’re fully caught up.
      </p>
      <button className="btn-primary mt-5 !px-5 !py-2 text-sm" onClick={onClose} data-testid="reconcile-empty-close">
        All done
      </button>
    </div>
  );
}

function FinishSummary({ tally, total, onFinish, finishing }) {
  return (
    <div className="text-center py-2" data-testid="reconcile-done-body">
      <div className="inline-flex items-center justify-center w-14 h-14 rounded-full bg-[#DDE9D8] text-[#1F6B2E] mb-3">
        <CheckCircle2 size={26} />
      </div>
      <h2 className="font-heading text-xl font-medium text-[#2C302E]" data-testid="reconcile-done-title">Reconciled ✓</h2>
      <p className="text-sm text-[#59605C] mt-1">Nice work — that’s the whole ritual done for this week.</p>

      <div className="mt-5 grid grid-cols-3 gap-2">
        <SummaryStat label="Cleared" value={tally.cleared} accent="#1F6B2E" bg="#DDE9D8" testId="reconcile-tally-cleared" />
        <SummaryStat label="Pending" value={tally.pending} accent="#8C5D30" bg="#F7EBD0" testId="reconcile-tally-pending" />
        <SummaryStat label="Skipped" value={tally.skipped} accent="#59605C" bg="#F2EFEB" testId="reconcile-tally-skipped" />
      </div>

      {tally.cleared > 0 && (
        <div className="mt-4 p-3 rounded-xl bg-[#F9F8F6] text-sm text-[#3E4340]" data-testid="reconcile-tally-amount">
          You marked <span className="font-semibold text-[#1F6B2E]">{formatMoney(tally.clearedAmount)}</span> as cleared across {tally.cleared} bill{tally.cleared === 1 ? "" : "s"}.
        </div>
      )}

      <button
        className="btn-primary mt-5 !px-5 !py-2 text-sm"
        onClick={onFinish}
        disabled={finishing}
        data-testid="reconcile-done-finish"
      >
        {finishing ? "Saving…" : `Finish (${total} reviewed)`}
      </button>
    </div>
  );
}

function SummaryStat({ label, value, accent, bg, testId }) {
  return (
    <div className="p-3 rounded-xl border-2" style={{ backgroundColor: bg, borderColor: accent }} data-testid={testId}>
      <div className="font-heading text-2xl font-semibold" style={{ color: accent }}>{value}</div>
      <div className="text-[10px] font-semibold uppercase tracking-widest" style={{ color: accent }}>{label}</div>
    </div>
  );
}
